import {
  isTaskLevelSchedule,
  ScheduleStatus,
  ScheduleType
} from '@dadohr/shared/domainTypes/schedules';
import { ONE_DAY } from '@dadohr/shared/domainTypes/time';
import { FS } from '@dadohr/shared/versions';
import {
  Button,
  Card,
  FormControlLabel,
  Switch,
  Typography
} from '@material-ui/core';
import { compact, groupBy, keyBy, mapValues, sortBy, uniq } from 'lodash';
import { useState } from 'react';
import { Loader } from '../../../../../components/Loader';
import { ChannelIcon } from '../../../../../features/Experiences/components/Channels';
import { useErrorLogger } from '../../../../../hooks/useErrorLogger';
import { usePromise } from '../../../../../hooks/usePromise';
import { CanvasBar } from '../../../../../layout/Canvas';
import { FlexContainer, FlexItem } from '../../../../../layout/Flex';
import { Section } from '../../../../../layout/Section';
import {
  getExperiencesByIdAsMap,
  getParticipantActor
} from '../../../../../services/experiences/instance';
import {
  getExperienceTemplateById,
  getTaskTemplateForTaskId
} from '../../../../../services/experiences/template';
import { store } from '../../../../../services/firebase';
import { getOrg } from '../../../../../services/orgs';
import { toScheduleDoc } from '../../../../../services/schedules/documents';
import {
  addToTs,
  formatDatePrecise,
  now,
  tsIsAfter
} from '../../../../../services/time';
import { getUsersAsMap, toFullName } from '../../../../../services/user';
import { LinkExternal } from '../../../../components/LinkExternal';
import { ROUTES } from '../../../../services/routes';

const EXCLUDED_ORGS = new Set([
  'rHVA-wDlT',
  '4ODulp8hb',
  'FREE_TIER_SAMPLE_CONTENT_ORG_PROD'
]);
const getOverdueReminderData = async (showOnlyNextDay: boolean) => {
  const ds = await store()
    .collection(FS.schedules)
    .where('type', '==', ScheduleType.TASK_LATE_REMINDER)
    .where('status', 'in', [ScheduleStatus.PENDING, ScheduleStatus.RETRY])
    .get()
    .then((s) =>
      s.docs
        .map(toScheduleDoc)
        .filter(isTaskLevelSchedule)
        .filter((s) => {
          return !EXCLUDED_ORGS.has(s.data.orgId);
        })
    )
    .then((ss) => {
      if (!showOnlyNextDay) {
        return ss;
      }
      const cutoff = addToTs(now(), ONE_DAY);
      return ss.filter((s) => !tsIsAfter(s.data.at, cutoff));
    });

  const orgIds = uniq(ds.map((d) => d.data.orgId));
  const tplIds = uniq(ds.map((d) => d.data.templateId));
  const instIds = uniq(ds.map((d) => d.data.experienceId));
  const [orgs, tpls, insts] = await Promise.all([
    Promise.all(orgIds.map((orgId) => getOrg(orgId))).then((os) =>
      keyBy(compact(os), (o) => o.id)
    ),
    Promise.all(
      tplIds.map((tplId) => getExperienceTemplateById(tplId))
    ).then((ts) => keyBy(compact(ts), (t) => t.id)),
    getExperiencesByIdAsMap(instIds)
  ]);

  const userIds = uniq(
    compact(
      compact(Object.values(insts)).map((i) => getParticipantActor(i)?.userId)
    )
  );
  const usersById = await getUsersAsMap(userIds);

  const schedsByTplInstanceId = groupBy(ds, (d) => d.data.experienceId);

  const byOrgId = groupBy(ds, (s) => s.data.orgId);
  console.log(mapValues(byOrgId, (ss) => ss.length));
  console.log('data', orgs, tpls, insts);
  console.log(Object.keys(insts).length);
  return Object.values(orgs).map((o) => {
    return {
      org: o,
      tpls: Object.values(tpls)
        .filter((tpl) => tpl.data.orgId === o.id)
        .map((tpl) => {
          const is = compact(Object.values(insts)).filter(
            (i) => i.data.templateId === tpl.id
          );
          return {
            tpl,
            instances: is.map((i) => {
              const scheds = schedsByTplInstanceId[i.id] || [];
              const p = getParticipantActor(i);
              const user = usersById[p?.userId || ''] || null;
              return {
                instance: i,
                user,
                schedules: scheds
              };
            })
          };
        })
    };
  });
};

export const PageLateRemindersOverview = () => {
  const [showOnlyNextDay, setShowOnlyNextDay] = useState(true);
  const [data, loading, error, forceReload] = usePromise(
    async () => getOverdueReminderData(showOnlyNextDay),
    [showOnlyNextDay]
  );

  useErrorLogger('Late reminders Overview', error);
  console.log(data);
  return (
    <Section>
      <CanvasBar>
        <div>
          <FormControlLabel
            label={'Show only next day'}
            control={
              <Switch
                checked={showOnlyNextDay}
                onChange={() => setShowOnlyNextDay((x) => !x)}
                color="primary"
              />
            }
          />
        </div>
        <div>
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => forceReload()}
          >
            Refresh
          </Button>
        </div>
      </CanvasBar>
      {loading || !data ? (
        <Loader height={500} />
      ) : (
        <FlexContainer
          fullWidth
          direction="column"
          justifyContent="flex-start"
          alignItems="flex-start"
          spacing={3}
        >
          {data.map((d) => {
            return (
              <Card key={d.org.id} style={{ width: '100%' }}>
                <Typography variant="h4">
                  {d.org.data.name}{' '}
                  <LinkExternal href={ROUTES(d.org.id).base.url()}>
                    {d.org.id}
                  </LinkExternal>
                </Typography>

                <FlexContainer
                  fullWidth
                  direction="column"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                  spacing={2}
                  marginTop={1}
                >
                  {d.tpls.map((tpl) => (
                    <FlexContainer
                      fullWidth
                      direction="column"
                      justifyContent="flex-start"
                      alignItems="flex-start"
                      spacing={1}
                      key={tpl.tpl.id}
                    >
                      <Typography variant="h6">
                        {tpl.tpl.data.adminTitle || tpl.tpl.data.title} -{' '}
                        <LinkExternal
                          href={ROUTES(
                            tpl.tpl.data.orgId
                          ).experiences.details.general.url(tpl.tpl.id)}
                        >
                          {tpl.tpl.id}
                        </LinkExternal>
                      </Typography>

                      <div style={{ marginLeft: 16 }}>
                        {tpl.instances.map((i) => (
                          <div style={{ marginTop: 16 }}>
                            <LinkExternal
                              href={ROUTES(
                                tpl.tpl.data.orgId
                              ).experiences.details.general.url(tpl.tpl.id, {
                                type: 'instance',
                                instanceId: i.instance.id
                              })}
                            >
                              {i.instance.id}
                            </LinkExternal>{' '}
                            - {i.user ? toFullName(i.user.data) : ''}
                            <div style={{ marginLeft: 16 }}>
                              {sortBy(i.schedules, (s) =>
                                s.data.at.toMillis()
                              ).map((s) => {
                                const title =
                                  getTaskTemplateForTaskId(
                                    tpl.tpl,
                                    i.instance,
                                    s.data.taskId
                                  )?.title || 'Task template not found';
                                return (
                                  <FlexContainer key={s.id}>
                                    <ChannelIcon
                                      channel={s.data.channel}
                                      size={16}
                                    />
                                    <div>{s.data.actor}</div>
                                    <div>|</div>
                                    <FlexItem flex={1}>{title}</FlexItem>
                                    <div>{formatDatePrecise(s.data.at)}</div>
                                  </FlexContainer>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </FlexContainer>
                  ))}
                </FlexContainer>
              </Card>
            );
          })}
        </FlexContainer>
      )}
    </Section>
  );
};
